import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../shared/api-client';

interface PathSignal {
  page: string | null;
  resourceId?: string;
}

function parsePathSignal(pathname: string): PathSignal {
  // /apps/chat or /apps/chat/:threadId
  const chatMatch = pathname.match(/^\/apps\/chat(?:\/(.+))?$/);
  if (chatMatch) return { page: 'chat', resourceId: chatMatch[1] ?? undefined };

  // /docs/:slug
  const docsMatch = pathname.match(/^\/docs\/(.+)$/);
  if (docsMatch) return { page: 'docs', resourceId: docsMatch[1] };

  // /docs (listing)
  if (pathname === '/docs') return { page: 'docs' };

  // /apps/:feature (anything else under /apps)
  const appsMatch = pathname.match(/^\/apps\/([^/]+)/);
  if (appsMatch) return { page: appsMatch[1] ?? null };

  // Admin pages — track the top-level section only (no resource IDs)
  if (pathname.startsWith('/admin')) return { page: 'admin' };

  return { page: null };
}

/**
 * Fires a page_view signal on every route change.
 * Must be rendered inside a BrowserRouter (needs useLocation).
 */
export function usePageViewSignal(): void {
  const location = useLocation();

  useEffect(() => {
    const { page, resourceId } = parsePathSignal(location.pathname);
    if (!page) return;
    api.logPageViewSignal(page, resourceId);
  }, [location.pathname]);
}

/**
 * Accumulates chat engagement dwell time for a given thread.
 * Only starts the heartbeat after the user has scrolled — prevents counting
 * people who have the tab open but aren't actively reading.
 */
export function useChatEngagement(threadId: string | undefined): void {
  useEffect(() => {
    if (!threadId) return;

    let hasScrolled = false;
    let intervalId: ReturnType<typeof setInterval> | null = null;

    const handleScroll = () => {
      if (hasScrolled) return;
      hasScrolled = true;
      intervalId = setInterval(() => {
        api.logChatEngagementSignal(threadId, 30);
      }, 30_000);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (intervalId !== null) clearInterval(intervalId);
    };
  }, [threadId]);
}
